import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../../components/ui/Card';
import { Key, Plug, Unplug, RefreshCw, Building2, AlertCircle, CheckCircle2, Loader2, Eye, EyeOff, BarChart3, TrendingUp } from 'lucide-react';

const API_BASE = 'https://api.glowytics.com';

const MONTHS_TR = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];

function formatCurrency(val) {
  if (val == null) return '₺0';
  return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 2 }).format(val);
}

function formatNumber(val) {
  if (val == null) return '0';
  return new Intl.NumberFormat('tr-TR').format(val);
}

export default function Criteo() {
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [advertisers, setAdvertisers] = useState([]);
  const [loadingAdvertisers, setLoadingAdvertisers] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [monthlyData, setMonthlyData] = useState(null);
  const [loadingMonthly, setLoadingMonthly] = useState(false);

  const brandId = localStorage.getItem('currentBrandId');

  const fetchHeaders = useCallback(() => ({
    'Content-Type': 'application/json',
    'X-Brand-ID': brandId,
  }), [brandId]);

  const checkStatus = useCallback(async () => {
    if (!brandId) return;
    try {
      const res = await fetch(`${API_BASE}/api/criteo/status`, {
        credentials: 'include',
        headers: fetchHeaders()
      });
      const data = await res.json();
      setConnectionStatus(data);
      if (data.connected) {
        fetchAdvertisers();
      }
    } catch (err) {
      console.error('Status check failed:', err);
    }
  }, [brandId, fetchHeaders]);

  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  const fetchAdvertisers = async () => {
    setLoadingAdvertisers(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/criteo/advertisers`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to fetch advertisers');
      setAdvertisers(data.advertisers || []);
    } catch (err) {
      console.error('Fetch advertisers failed:', err);
      setError(err.message);
    } finally {
      setLoadingAdvertisers(false);
    }
  };

  const fetchMonthlySpend = async () => {
    setLoadingMonthly(true);
    setError('');
    try {
      const advId = advertisers.length > 0 ? advertisers[0].id : undefined;
      const res = await fetch(`${API_BASE}/api/criteo/statistics`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
        body: JSON.stringify({
          advertiserIds: advId,
          startDate: '2025-01-01T00:00:00Z',
          endDate: '2025-12-31T00:00:00Z',
          dimensions: ['advertiserId', 'month'],
          metrics: ['Clicks', 'Displays', 'AdvertiserCost'],
          currency: 'TRY',
          format: 'JSON',
          timezone: 'Europe/Istanbul'
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to fetch statistics');
      setMonthlyData(data.data);
    } catch (err) {
      console.error('Fetch monthly spend failed:', err);
      setError(err.message);
    } finally {
      setLoadingMonthly(false);
    }
  };

  const handleConnect = async (e) => {
    e.preventDefault();
    setConnecting(true);
    setError('');
    setSuccess('');
    try {
      const res = await fetch(`${API_BASE}/api/criteo/connect`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
        body: JSON.stringify({ clientId, clientSecret })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Connection failed');
      setSuccess('Criteo connected successfully!');
      setClientId('');
      setClientSecret('');
      setShowSecret(false);
      await checkStatus();
    } catch (err) {
      setError(err.message);
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect Criteo?')) return;
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/criteo/disconnect`, {
        method: 'DELETE',
        credentials: 'include',
        headers: fetchHeaders()
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Disconnect failed');
      setConnectionStatus(null);
      setAdvertisers([]);
      setMonthlyData(null);
      setSuccess('Criteo disconnected');
    } catch (err) {
      setError(err.message);
    }
  };

  const parsedMonthly = React.useMemo(() => {
    if (!monthlyData) return null;
    let rows = [];
    if (monthlyData?.Rows) {
      rows = monthlyData.Rows;
    } else if (Array.isArray(monthlyData)) {
      rows = monthlyData;
    } else if (monthlyData?.rows) {
      rows = monthlyData.rows;
    } else if (monthlyData?.Columns) {
      return { columns: monthlyData.Columns, rows: monthlyData.Rows || [] };
    }
    return rows;
  }, [monthlyData]);

  const maxSpend = React.useMemo(() => {
    if (!parsedMonthly || !Array.isArray(parsedMonthly)) return 0;
    return Math.max(...parsedMonthly.map(r => parseFloat(r.AdvertiserCost || r.Spend || r.spend || 0)), 1);
  }, [parsedMonthly]);

  const totalSpend = React.useMemo(() => {
    if (!parsedMonthly || !Array.isArray(parsedMonthly)) return 0;
    return parsedMonthly.reduce((sum, r) => sum + parseFloat(r.AdvertiserCost || r.Spend || r.spend || 0), 0);
  }, [parsedMonthly]);

  const totalClicks = React.useMemo(() => {
    if (!parsedMonthly || !Array.isArray(parsedMonthly)) return 0;
    return parsedMonthly.reduce((sum, r) => sum + parseInt(r.Clicks || r.clicks || 0), 0);
  }, [parsedMonthly]);

  const totalDisplays = React.useMemo(() => {
    if (!parsedMonthly || !Array.isArray(parsedMonthly)) return 0;
    return parsedMonthly.reduce((sum, r) => sum + parseInt(r.Displays || r.displays || 0), 0);
  }, [parsedMonthly]);

  function getMonthLabel(row) {
    const m = row.Month || row.month || '';
    if (!m) return m;
    const parts = m.split('-');
    if (parts.length >= 2) {
      const monthIdx = parseInt(parts[1], 10) - 1;
      return MONTHS_TR[monthIdx] || m;
    }
    return m;
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold">Criteo</h1>
        {connectionStatus?.connected && (
          <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 px-3 py-1.5 rounded-full">
            <CheckCircle2 className="w-4 h-4" />
            Connected
          </div>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-xl text-green-700 text-sm">
          <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
          <span>{success}</span>
        </div>
      )}

      {/* Connection Form */}
      <Card className="p-6 rounded-2xl">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
            <Key className="w-5 h-5 text-orange-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Criteo API Credentials</h2>
            <p className="text-sm text-gray-500">
              {connectionStatus?.connected
                ? `Connected with Client ID: ${connectionStatus.clientId?.substring(0, 12)}...`
                : 'Enter your Criteo client credentials from partners.criteo.com'}
            </p>
          </div>
        </div>

        {!connectionStatus?.connected ? (
          <form onSubmit={handleConnect} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Client ID</label>
              <input
                type="text"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                placeholder="e.g. d99923e3700b4b1d9a57177be4a70b33"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Client Secret</label>
              <div className="relative">
                <input
                  type={showSecret ? 'text' : 'password'}
                  value={clientSecret}
                  onChange={(e) => setClientSecret(e.target.value)}
                  placeholder="Enter your client secret"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all text-sm pr-12"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowSecret(!showSecret)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={connecting || !clientId || !clientSecret}
              className="flex items-center gap-2 px-6 py-2.5 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-xl transition-colors text-sm font-medium"
            >
              {connecting ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Connecting...</>
              ) : (
                <><Plug className="w-4 h-4" /> Connect to Criteo</>
              )}
            </button>
          </form>
        ) : (
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={fetchAdvertisers}
              className="flex items-center gap-2 px-4 py-2 bg-orange-50 hover:bg-orange-100 text-orange-700 rounded-xl transition-colors text-sm font-medium"
            >
              <RefreshCw className={`w-4 h-4 ${loadingAdvertisers ? 'animate-spin' : ''}`} />
              Refresh Advertisers
            </button>
            <button
              onClick={fetchMonthlySpend}
              disabled={loadingMonthly}
              className="flex items-center gap-2 px-4 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-xl transition-colors text-sm font-medium disabled:opacity-50"
            >
              {loadingMonthly ? <Loader2 className="w-4 h-4 animate-spin" /> : <BarChart3 className="w-4 h-4" />}
              2025 Aylik Harcama
            </button>
            <button
              onClick={handleDisconnect}
              className="flex items-center gap-2 px-4 py-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-xl transition-colors text-sm font-medium"
            >
              <Unplug className="w-4 h-4" />
              Disconnect
            </button>
          </div>
        )}
      </Card>

      {/* Advertisers List */}
      {connectionStatus?.connected && advertisers.length > 0 && (
        <Card className="p-6 rounded-2xl">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Advertiser Accounts</h2>
              <p className="text-sm text-gray-500">{advertisers.length} advertiser account(s) found</p>
            </div>
          </div>

          {loadingAdvertisers ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Advertiser ID</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Name</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Type</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {advertisers.map((adv, i) => (
                    <tr key={adv.id || i} className="hover:bg-gray-50 transition-colors">
                      <td className="py-3 px-4 text-sm font-mono text-gray-800">{adv.id}</td>
                      <td className="py-3 px-4 text-sm font-medium">{adv.name}</td>
                      <td className="py-3 px-4">
                        <span className="text-xs bg-orange-50 text-orange-700 px-2 py-1 rounded-full">
                          {adv.type || 'Advertiser'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* Monthly Spend 2025 */}
      {monthlyData && (
        <Card className="p-6 rounded-2xl">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">2025 Aylik Harcama Dokumu</h2>
              <p className="text-sm text-gray-500">Criteo reklam harcamasi - aylik detay</p>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4">
              <p className="text-sm text-orange-600 font-medium">Toplam Harcama</p>
              <p className="text-2xl font-bold text-orange-800">{formatCurrency(totalSpend)}</p>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4">
              <p className="text-sm text-blue-600 font-medium">Toplam Tiklama</p>
              <p className="text-2xl font-bold text-blue-800">{formatNumber(totalClicks)}</p>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4">
              <p className="text-sm text-green-600 font-medium">Toplam Gosterim</p>
              <p className="text-2xl font-bold text-green-800">{formatNumber(totalDisplays)}</p>
            </div>
          </div>

          {Array.isArray(parsedMonthly) && parsedMonthly.length > 0 ? (
            <>
              {/* Visual Bar Chart */}
              <div className="space-y-3 mb-6">
                {parsedMonthly.map((row, i) => {
                  const spend = parseFloat(row.AdvertiserCost || row.Spend || row.spend || 0);
                  const pct = maxSpend > 0 ? (spend / maxSpend) * 100 : 0;
                  return (
                    <div key={i} className="flex items-center gap-3">
                      <span className="text-sm font-medium text-gray-600 w-20 text-right">{getMonthLabel(row)}</span>
                      <div className="flex-1 bg-gray-100 rounded-full h-8 overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-orange-400 to-orange-600 rounded-full flex items-center justify-end pr-3 transition-all duration-500"
                          style={{ width: `${Math.max(pct, 2)}%` }}
                        >
                          {pct > 15 && <span className="text-xs font-medium text-white">{formatCurrency(spend)}</span>}
                        </div>
                      </div>
                      {pct <= 15 && <span className="text-xs font-medium text-gray-600">{formatCurrency(spend)}</span>}
                    </div>
                  );
                })}
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Ay</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">Harcama (TRY)</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">Tiklama</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">Gosterim</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">CTR</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {parsedMonthly.map((row, i) => {
                      const clicks = parseInt(row.Clicks || row.clicks || 0);
                      const displays = parseInt(row.Displays || row.displays || 0);
                      const spend = parseFloat(row.AdvertiserCost || row.Spend || row.spend || 0);
                      const ctr = displays > 0 ? ((clicks / displays) * 100).toFixed(2) : '0.00';
                      return (
                        <tr key={i} className="hover:bg-gray-50 transition-colors">
                          <td className="py-3 px-4 text-sm font-medium">{getMonthLabel(row)}</td>
                          <td className="py-3 px-4 text-sm text-right font-semibold text-orange-700">{formatCurrency(spend)}</td>
                          <td className="py-3 px-4 text-sm text-right">{formatNumber(clicks)}</td>
                          <td className="py-3 px-4 text-sm text-right">{formatNumber(displays)}</td>
                          <td className="py-3 px-4 text-sm text-right">{ctr}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-gray-300 bg-gray-50 font-semibold">
                      <td className="py-3 px-4 text-sm">Toplam</td>
                      <td className="py-3 px-4 text-sm text-right text-orange-700">{formatCurrency(totalSpend)}</td>
                      <td className="py-3 px-4 text-sm text-right">{formatNumber(totalClicks)}</td>
                      <td className="py-3 px-4 text-sm text-right">{formatNumber(totalDisplays)}</td>
                      <td className="py-3 px-4 text-sm text-right">
                        {totalDisplays > 0 ? ((totalClicks / totalDisplays) * 100).toFixed(2) : '0.00'}%
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <BarChart3 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="font-medium">Veri bulunamadi</p>
              <p className="text-sm mt-1">API yaniti beklenen formatta degil. Raw data:</p>
              <pre className="mt-3 p-4 bg-gray-50 rounded-xl text-xs text-left overflow-auto max-h-64">
                {JSON.stringify(monthlyData, null, 2)}
              </pre>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
